import { useTranslation } from 'react-i18next';
import ServiceLayout from './components/ServiceLayout';
import useSeoPage from '@/hooks/useSeoPage';

export default function OrthopedicPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  useSeoPage({
    title: 'Ортопедическая стоматология Кишинёв | Протезирование зубов | Zimbet32',
    description: 'Протезирование зубов в Кишинёве: коронки из циркония, керамические виниры, мостовидные протезы. Zimbet32 — stomatologie ortopedică de calitate europeană în Chișinău.',
    keywords: 'протезирование зубов Кишинёв, коронки на зубы, виниры, мостовидный протез, stomatologie ortopedică Chișinău, Zimbet32',
    canonicalPath: '/services/orthopedic',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'MedicalProcedure',
      name: 'Ортопедическая стоматология',
      url: `${siteUrl}/services/orthopedic`,
      description: 'Протезирование зубов, установка коронок, виниров и мостовидных конструкций в Кишинёве.',
      procedureType: 'https://schema.org/TherapeuticProcedure',
      bodyLocation: 'Зубы',
      provider: {
        '@type': 'Dentist',
        name: 'Zimbet32',
        url: siteUrl,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Strada București 60',
          addressLocality: 'Chișinău',
          postalCode: 'MD-2012',
          addressCountry: 'MD',
        },
        telephone: '+373-22-22-07-09',
      },
    },
  });

  const procedures = [
    {
      icon: 'ri-contrast-drop-2-line',
      title: t('servicePages.orthopedic.procedure1'),
      description: t('servicePages.orthopedic.procedure1Desc')
    },
    {
      icon: 'ri-grid-line',
      title: t('servicePages.orthopedic.procedure2'),
      description: t('servicePages.orthopedic.procedure2Desc')
    },
    {
      icon: 'ri-layout-grid-line',
      title: t('servicePages.orthopedic.procedure3'),
      description: t('servicePages.orthopedic.procedure3Desc')
    },
    {
      icon: 'ri-flashlight-line',
      title: t('servicePages.orthopedic.procedure4'),
      description: t('servicePages.orthopedic.procedure4Desc')
    },
    {
      icon: 'ri-stack-line',
      title: t('servicePages.orthopedic.procedure5'),
      description: t('servicePages.orthopedic.procedure5Desc')
    },
    {
      icon: 'ri-paint-brush-line',
      title: t('servicePages.orthopedic.procedure6'),
      description: t('servicePages.orthopedic.procedure6Desc')
    }
  ];

  const advantages = [
    {
      icon: 'ri-star-smile-line',
      title: t('servicePages.orthopedic.benefit1'),
      description: t('servicePages.orthopedic.benefit1Desc')
    },
    {
      icon: 'ri-time-line',
      title: t('servicePages.orthopedic.benefit2'),
      description: t('servicePages.orthopedic.benefit2Desc')
    },
    {
      icon: 'ri-medal-line',
      title: t('servicePages.orthopedic.benefit3'),
      description: t('servicePages.orthopedic.advantage3Desc')
    }
  ];

  const galleryImages = [
    'https://public.readdy.ai/ai/img_res/b4500980c906f63163b0610f435445af.jpg',
    'https://readdy.ai/api/search-image?query=Beautiful%20ceramic%20dental%20veneers%20installation%20result%2C%20perfect%20white%20smile%20close-up%2C%20natural%20looking%20teeth%20restoration%2C%20aesthetic%20dentistry%20outcome%2C%20professional%20dental%20work%2C%20patient%20satisfaction%2C%20bright%20clean%20background&width=800&height=600&seq=orthopedic-gallery-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Dental%20bridge%20prosthetic%20construction%20process%2C%20metal-ceramic%20framework%2C%20precision%20dental%20work%2C%20professional%20laboratory%20setting%2C%20modern%20dental%20materials%2C%20detailed%20craftsmanship%2C%20quality%20dental%20restoration%20components&width=800&height=600&seq=orthopedic-gallery-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Complete%20dental%20crown%20preparation%20procedure%2C%20dentist%20working%20on%20tooth%20preparation%2C%20professional%20dental%20instruments%2C%20modern%20clinic%20setting%2C%20precision%20work%2C%20patient%20in%20dental%20chair%2C%20sterile%20environment%2C%20quality%20dental%20care&width=800&height=600&seq=orthopedic-gallery-4&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Zirconia%20dental%20crowns%20set%2C%20high-quality%20ceramic%20prosthetics%2C%20natural%20tooth%20color%2C%20modern%20dental%20materials%2C%20professional%20dental%20laboratory%20products%2C%20precision%20crafted%20restorations%2C%20clean%20white%20background%20display&width=800&height=600&seq=orthopedic-gallery-5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Happy%20patient%20showing%20perfect%20smile%20after%20dental%20prosthetics%20installation%2C%20successful%20dental%20restoration%20result%2C%20natural%20looking%20teeth%2C%20satisfied%20customer%2C%20modern%20dental%20clinic%2C%20professional%20dental%20care%20outcome&width=800&height=600&seq=orthopedic-gallery-6&orientation=landscape'
  ];

  return (
    <ServiceLayout
      heroImage="https://readdy.ai/api/search-image?query=Modern%20dental%20orthopedic%20prosthetics%20clinic%2C%20professional%20dental%20laboratory%20with%20advanced%20equipment%2C%20ceramic%20crowns%20and%20veneers%20display%2C%20precision%20dental%20work%20environment%2C%20contemporary%20healthcare%20facility%2C%20clean%20organized%20workspace%2C%20quality%20dental%20restoration%20materials&width=1920&height=600&seq=orthopedic-hero&orientation=landscape"
      breadcrumbKey="servicePages.orthopedic.title"
      titleKey="servicePages.orthopedic.title"
      descriptionKey="servicePages.orthopedic.description"
      procedures={procedures}
      advantages={advantages}
      galleryImages={galleryImages}
      ctaTitleKey="servicePages.orthopedic.ctaTitle"
      ctaDescriptionKey="servicePages.orthopedic.ctaDescription"
      ctaButtonKey="servicePages.orthopedic.ctaButton"
    />
  );
}